/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/**
 * Required to use React components.
 */
import * as React from 'react';

/**
 * Used for the basic page layout.
 */
import {
    EventDefinitionsContext,
    FormDefinitionsContext,
    FormType,
    IEventDefinition,
    IFormDefinition,
    MarkdownView,
    PatientContext,
    RouteLoading,
    usePatientSummary
} from '@ngt/opms';

import { Link, Navigate, useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons/faCheckCircle';

import { faSignOutAlt } from '@fortawesome/pro-duotone-svg-icons/faSignOutAlt';

import { faChevronRight } from '@fortawesome/pro-duotone-svg-icons/faChevronRight';

import { faTimesCircle } from '@fortawesome/pro-duotone-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle';
import { Alert, AlertTitle, Box, Button, darken, lighten, ListItem, ListItemIcon, ListItemText, Paper, styled, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDefinitionType } from '../api/dtos';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IProStatus {
    eventDefinition: IEventDefinition | undefined;
    formDefinition: IFormDefinition | undefined;
    eventRepeat: number | undefined;
    formRepeat: number | undefined;
    completed: boolean | undefined;
}

interface IProProgressProps {
    proStatuses: IProStatus[];
}

interface IPatientProsProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3, 3, 0, 3),

    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0),
        border: 'none',
        boxShadow: 'none'
    }
}))

const StyledWrapper = styled(Box)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        background: theme.palette.grey[100],

        ...theme.applyStyles('dark', {
            background: theme.palette.grey[900],
        })
    }
}))

const StyledWelcome = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),

    [theme.breakpoints.up('md')]: {
        maxWidth: 800,
        margin: 'auto'
    }
}))

const BoldParagraph = styled(Typography)(({theme}) => ({
    paddingBottom: theme.spacing(1.5),
    textAlign: 'center'
}))

const Paragraph = styled(Typography)(({theme}) => ({
    paddingBottom: theme.spacing(1.5),
}))


const StyledContinue = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0, 3, 3, 3),
    textAlign: 'center',
}))


const StyledQuestionnaireInfo = styled(Box)(({ theme }) => ({
    paddingBottom: theme.spacing(1.5)
}))


const StyledInfoPanel = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(1.5)
}))


const StyledButtons = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    textAlign: 'center',

    [theme.breakpoints.up('md')]: {
        textAlign: 'right'
    }
}))


const StyledCompleteProgress = styled(ListItem)(({ theme }) => {
    const getBackgroundColor = theme.palette.mode === 'light' ? lighten : darken;

    return {
        border: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: getBackgroundColor(theme.palette.success.main, 0.9)
    }
})

const StyledIncompleteProgress = styled(ListItem)(({ theme }) => {
    const getBackgroundColor = theme.palette.mode === 'light' ? lighten : darken;

    return {
        border: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: getBackgroundColor(theme.palette.warning.main, 0.9)
    }
})

const StyledHeading = styled(Typography)(({theme}) => ({
    fontWeight: 'bold',
    paddingBottom: theme.spacing(1)
}))

const StyledProgressContainer = styled(Typography)(({theme}) => ({
    paddingBottom: theme.spacing(1.5)
}))

const StyledProgressIcon = styled(ListItemIcon)(({theme}) => ({
    marginLeft: 'auto',
    minWidth: 0,
    color: theme.palette.success.main
}))

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

const formDefinitionTranslationKeyMap = {
    [FormDefinitionType.BreastCancerTreatmentOutcomesScale]: "breastCancerTreatmentOutcomesScale",
    [FormDefinitionType.BriefIllnessPerceptionQuestionnaire]: "briefIllnessPerceptionQuestionnaire",
    [FormDefinitionType.DecisionRegretScale]: "decisionRegretScale",
    [FormDefinitionType.EndocrineTherapyAdherence]: "endocrineTherapyAdherence",
    [FormDefinitionType.EORTCIL353]: "eortcil353",
    [FormDefinitionType.EQ5D5L]: "eq5d5l",
    [FormDefinitionType.ExperienceAndImpactOfTreatment]: "experienceAndImpactOfTreatment",
    [FormDefinitionType.FearOfCancerRecurrence]: "fearOfCancerRecurrence",
    [FormDefinitionType.GeneralizedAnxietyDisorder2]: "generalizedAnxietyDisorder2",
    [FormDefinitionType.InternationalPersonalityItemPool]: "internationalPersonalityItemPool",
    [FormDefinitionType.PatientHealthQuestionnaire]: "patientHealthQuestionnaire2",
    [FormDefinitionType.RiskAppraisal]: "riskAppraisalForm",
}

const ProProgress: React.FunctionComponent<IProProgressProps> = ({
    proStatuses
}) => {

    const { t } = useTranslation();

    const translatedPageTitle = useMemo(() => t("patientProsPage.formList.title", { defaultValue: "**Forms for this visit**"}), [t])
    
    const items = proStatuses.map((ps, index) => {
        const Component = ps.completed ? StyledCompleteProgress : StyledIncompleteProgress;

        return (
            <Component
                key={`ps-${index}`}
            >
                <ListItemText
                    primary={ps.formDefinition?.name}
                />
                {
                    ps.completed === true && (
                        <StyledProgressIcon>
                            <FontAwesomeIcon fixedWidth icon={faCheckCircle} size="2x" />
                        </StyledProgressIcon>
                    )
                }
            </Component>
        );
    })

    return (
        <StyledProgressContainer>
            <StyledHeading>
                <MarkdownView>{translatedPageTitle}</MarkdownView>
            </StyledHeading>
            {
                items
            }
        </StyledProgressContainer>
    );
}

const PatientProsPage: React.FunctionComponent<IPatientProsProps> = () => {
    const { data: eventDefinitions } = React.useContext(EventDefinitionsContext);
    const { data: formDefinitions } = React.useContext(FormDefinitionsContext);
    const { data: patient } = React.useContext(PatientContext);
    const { data, loading, validating, error } = usePatientSummary(patient?.id);

    const { t } = useTranslation();

    const params = useParams();
    const next = params.next === 'next';

    const latestPros = useMemo(() => {
        if (!data || !data.events || loading || validating) {
            return [];
        }

        const proEvents = data.events.filter(er => {
            if (!er.eventState.validate ||
                !er.eventState.write ||
                !er.forms ||
                er.forms.length === 0
            ) {
                return false;
            }

            return er.forms.some(fr => {
                const formDefinition = formDefinitions?.find(fd => fd.id === fr.formState.formDefinitionId);
                return formDefinition?.type === FormType.Pro;
            })
        });

        if (proEvents.length === 0) {
            return [];
        }

        const latestProEvent = proEvents[proEvents.length - 1];

        const eventDefinition = eventDefinitions?.find(ed => ed.id === latestProEvent.eventState.eventDefinitionId);

        return latestProEvent
            .forms?.map(fr => {
                const formDefinition = formDefinitions?.find(fd => fd.id === fr.formState.formDefinitionId)
                const formDefinitionKey = formDefinition?.id ? 
                    formDefinitionTranslationKeyMap[formDefinition.id as keyof typeof formDefinitionTranslationKeyMap] :
                    undefined;

                if(formDefinitionKey && formDefinition?.type === FormType.Pro) {
                    const formDefinitionNameTranslation = t(`forms.${formDefinitionKey}.name`)
                    formDefinition.name = formDefinitionNameTranslation
                }

                return {
                    eventDefinition,
                    formDefinition: formDefinition,
                    eventRepeat: latestProEvent.eventState.eventRepeat,
                    formRepeat: fr.formState.formRepeat,
                    completed: fr.formValidation?.completed
                }
            })
            .filter(forms => forms.formDefinition?.type === FormType.Pro) ?? [];
    }, [data, eventDefinitions, formDefinitions, t]);

    const completed = useMemo(() => {
        return !latestPros.some(lp => !lp.completed);
    }, [latestPros]);

    const nextPro = useMemo(() => {
        if (!latestPros) {
            return null;
        }
        const nextPro = latestPros.find(lp => !lp.completed);
        
        if (!nextPro) {
            return null;
        }

        return nextPro;
    }, [latestPros])

    const translatedPageTitle = useMemo(() => t("patientProsPage.info.title", { defaultValue: "**Welcome to the BCT 2401 — PROSPECTIVE Patient questionnaire platform.**"}), [t])
    const translatedParagraph1 = useMemo(() => t("patientProsPage.info.paragraph.1", { defaultValue: "On the following screens will be a series of topics with related questions which you have agreed to complete as part of your participation in the **PROSPECTIVE** research study"}), [t])
    const translatedParagraph2 = useMemo(() => t("patientProsPage.info.paragraph.2", { defaultValue: "As you complete each series of questions the platform will automatically progress you through the required topics until you reach the end."}), [t])
    const translatedParagraph3 = useMemo(() => t("patientProsPage.info.paragraph.3", { defaultValue: "You can monitor your progress through the questions via the progress bar at the bottom of the screen.  You will be presented with a completion screen once all questions have been answered."}), [t])
    const translatedThankYou = useMemo(() => t("patientProsPage.info.thankYou", { defaultValue: "**Thank you for taking the time to complete this series of questions.**"}), [t])
    const translatedAlert = useMemo(() => t("patientProsPage.info.alert", { defaultValue: "Individual questionnaire response will not be viewed by a member of the treatment team.If you have any concerns please report it directly to your treatment team."}), [t])
    const translatedContinueButtonText = useMemo(() => t("patientProsPage.info.continueButton", { defaultValue: "Continue"}), [t])

    if (loading || validating) {
        return (
            <RouteLoading />
        )
    }

    if (error) {
        return (
            <StyledWrapper>
                <StyledWelcome>
                    <StyledPaper elevation={3}>        
                        <Alert severity="error" icon={<FontAwesomeIcon fixedWidth icon={faTimesCircle} />} >
                            <AlertTitle>
                                Error
                            </AlertTitle>
                            An error occurred while attempting to load the questionnaires.
                        </Alert>
                        <StyledButtons>
                            <Button
                                color="primary"
                                variant="contained"
                                component="a"
                                href="/auth/logout"
                            >
                                Logout <FontAwesomeIcon fixedWidth icon={faSignOutAlt} />
                            </Button>
                        </StyledButtons>
                    </StyledPaper>
                </StyledWelcome>
            </StyledWrapper>
        )
    }

    return (
        <>
            {
                !completed && !!next && nextPro && (
                    <Navigate replace to={`/${nextPro?.eventDefinition?.code}/${nextPro?.eventRepeat}/${nextPro?.formDefinition?.code}/${nextPro?.formRepeat}`} />
                )
            }
            {
                !completed && !next && (
                    <StyledWrapper>
                        <StyledWelcome>
                            <StyledPaper elevation={3}>
                                <BoldParagraph>
                                    <MarkdownView>{translatedPageTitle}</MarkdownView>
                                </BoldParagraph>
                                <Paragraph>
                                    <MarkdownView>{translatedParagraph1}</MarkdownView>
                                </Paragraph>
                                <Paragraph>
                                    <MarkdownView>{translatedParagraph2}</MarkdownView>
                                </Paragraph>
                                <Paragraph>
                                    <MarkdownView>{translatedParagraph3}</MarkdownView>
                                </Paragraph>
                                <BoldParagraph>
                                    <MarkdownView>{translatedThankYou}</MarkdownView>
                                </BoldParagraph>
                                <StyledQuestionnaireInfo>
                                    <Alert
                                        severity="info"
                                        icon={<FontAwesomeIcon fixedWidth icon={faInfoCircle} />}
                                    >
                                        <MarkdownView>{translatedAlert}</MarkdownView>
                                    </Alert>
                                </StyledQuestionnaireInfo>
                                <ProProgress
                                    proStatuses={latestPros}
                                />
                                <StyledContinue>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        component={Link}
                                        to={`/${nextPro?.eventDefinition?.code}/${nextPro?.eventRepeat}/${nextPro?.formDefinition?.code}/${nextPro?.formRepeat}`}
                                    >
                                        <MarkdownView>{translatedContinueButtonText}</MarkdownView> <FontAwesomeIcon fixedWidth icon={faChevronRight} />
                                    </Button>
                                </StyledContinue>
                            </StyledPaper>
                        </StyledWelcome>
                    </StyledWrapper>
                )
            }
            {
                !!completed && (
                    <>
                        <StyledWrapper>
                            <StyledWelcome>
                                <StyledPaper elevation={3}>        
                                    <Alert severity="success" icon={<FontAwesomeIcon fixedWidth icon={faCheckCircle} />} >
                                        <AlertTitle>
                                            PROs Complete
                                        </AlertTitle>
                                        Thank you for taking the time to complete the PRO forms.
                                    </Alert>
                                    <StyledInfoPanel>
                                        <Alert severity="info" icon={<FontAwesomeIcon fixedWidth icon={faInfoCircle} />} >
                                            Individual questionnaire response will not be viewed by a member of the treatment team.
                                            If you have any concerns please report it directly to your treatment team.
                                        </Alert>
                                    </StyledInfoPanel>
                                    <StyledButtons>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            component="a"
                                            href="/auth/logout"
                                        >
                                            Logout <FontAwesomeIcon fixedWidth icon={faSignOutAlt} />
                                        </Button>
                                    </StyledButtons>
                                </StyledPaper>
                            </StyledWelcome>
                        </StyledWrapper>
                    </>
                )
            }
        </>
    );
}

/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default PatientProsPage;
